/**
 * CreateRoomPill — Inline gold pill button used in the Run-A-Room section header.
 * Reusable: any "primary inline action next to a section title" can use this.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';

interface CreateRoomPillEvents {
  'tap': void;
}

export class CreateRoomPill extends EventEmitter<CreateRoomPillEvents> {
  public element: HTMLButtonElement;

  constructor(label: string = 'CREATE') {
    super();
    CreateRoomPill.injectStyles();

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sdl-create-pill';
    btn.dataset.sound = 'button';
    btn.innerHTML = `
      <span class="sdl-create-pill-plus">+</span>
      <span class="sdl-create-pill-label">${escapeHtml(label)}</span>
    `;
    btn.addEventListener('click', () => this.emit('tap', undefined));
    this.element = btn;
  }

  setLabel(label: string): void {
    const labelEl = this.element.querySelector('.sdl-create-pill-label');
    if (labelEl) labelEl.textContent = label;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private static injectStyles(): void {
    if (document.getElementById('sdl-create-pill-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-create-pill-styles';
    style.textContent = `
      .sdl-create-pill {
        all: unset;
        position: relative;
        overflow: hidden;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px 7px 12px;
        border-radius: 999px;
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border: 1px solid var(--gold-light);
        box-shadow: 0 4px 14px rgba(255,215,0,0.22), inset 0 1px 0 rgba(255,255,255,0.3);
        cursor: pointer;
        transition: transform 0.15s ease;
      }
      .sdl-create-pill:active { transform: scale(0.95); }
      .sdl-create-pill::after {
        content: '';
        position: absolute;
        top: 0; left: -100%;
        width: 60%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
        animation: sdl-create-pill-shine 3s ease-in-out infinite;
        pointer-events: none;
      }
      @keyframes sdl-create-pill-shine {
        0%, 100% { left: -100%; }
        50% { left: 130%; }
      }
      .sdl-create-pill-plus {
        font-family: var(--font-display);
        font-size: 18px;
        color: #1a1300;
        line-height: 1;
        font-weight: 700;
      }
      .sdl-create-pill-label {
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 2px;
        color: #1a1300;
        line-height: 1;
      }
    `;
    document.head.appendChild(style);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c] || c));
}
